import {
  ReactElement,
  useState,
  useMemo,
  useCallback,
  useEffect,
  ChangeEvent,
} from 'react';

import { FormHandles } from '@unform/core';
import { toast } from 'react-toastify';

import { IconCamera } from '~/assets/icons';
import { Input, InputMask, Select } from '~/components';
import { useLoading } from '~/hooks/loading';
import { ICity, IState, UpdatedLogo } from '~/models/Common';
import { IUser, IUserCadasterRequiredForm } from '~/models/User';
import { ProfessionalService } from '~/services';
import LocationService from '~/services/Location';
import { getCEP } from '~/utils';

import * as S from './styles';

interface IEditUserFormProps {
  formName: string;
  formRef: React.RefObject<FormHandles>;
  handleSubmit: (data: IUserCadasterRequiredForm) => Promise<void>;
  userId?: string;
}

function EditUserForm({
  formName,
  formRef,
  handleSubmit,
  userId,
}: IEditUserFormProps): ReactElement {
  const { setLoading } = useLoading();

  const [user, setUser] = useState<IUser>();
  const [uploadImage, setUploadImage] = useState<UpdatedLogo>();
  const [states, setStates] = useState<IState[]>();
  const [cities, setCities] = useState<ICity[]>();
  const [lastCEP, setLastCEP] = useState('');
  const [initialData, setInitialData] = useState<IUserCadasterRequiredForm>({
    cpf: '',
    name: '',
    email: '',
    birth_date: '',
    cellphone: '',
    image_url: '',
    public_name: '',
    cref: '',
    address_country: '',
    address_state: '',
    address_city: '',
    address_zip_code: '',
    address_street: '',
    address_number: 0,
    address_complement: '',
  });
  const handleGetUser = useCallback(async () => {
    setLoading(true);

    if (userId) {
      const response = await ProfessionalService.getOneUser(userId);
      setUser(response.data);
    }

    setLoading(false);
  }, [userId]);

  const handleGetStates = useCallback(() => {
    setLoading(true);

    const response = LocationService.getStates();

    setStates(response);

    setLoading(false);
  }, []);

  const handleGetCities = useCallback(async (selectState: string) => {
    setLoading(true);

    const response = await LocationService.getCities(selectState);

    setCities(response.data);

    setLoading(false);
  }, []);

  const handleCEP = useCallback(
    async ({
      target: { value: cepWithMark },
    }: React.ChangeEvent<HTMLInputElement>) => {
      try {
        setLoading(true);

        const addressViaCep = await getCEP(cepWithMark, lastCEP);
        setLastCEP(cepWithMark);
        setInitialData({
          ...initialData,
          address_city: addressViaCep.address_city,
          address_state: addressViaCep.address_state,
          address_street: addressViaCep.address_street,
          address_complement: addressViaCep.address_complement,
        });
        formRef.current?.setFieldValue(
          'address_state',
          addressViaCep.address_state,
        );
        handleGetCities(addressViaCep.address_state ?? '').then(() => {
          formRef.current?.setFieldValue(
            'address_city',
            addressViaCep.address_city,
          );
        });

        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error(`${error}`);
      }
    },
    [formRef, handleGetCities, initialData, lastCEP],
  );

  const handleSetImage = useCallback(
    ({ target: { files } }: ChangeEvent<HTMLInputElement>) => {
      if (files) {
        const file = files[0];
        setUploadImage({
          name: URL.createObjectURL(file),
          file,
        });
      }
    },
    [setUploadImage],
  );

  const statesOptionsMemo = useMemo(
    () =>
      states?.map(({ sigla, nome }) => ({
        label: nome,
        value: sigla,
      })),
    [states],
  );

  const citiesOptionsMemo = useMemo(
    () =>
      cities?.map(({ nome }) => ({
        label: nome,
        value: nome,
      })),
    [cities],
  );

  useEffect(() => {
    handleGetUser();
  }, [handleGetUser, userId]);

  useEffect(() => {
    handleGetStates();
  }, [handleGetStates]);

  useEffect(() => {
    if (user) {
      setUploadImage({
        name: user.image_url,
      });
      setInitialData({
        public_name: user.professional?.public_name,
        name: user.name,
        cpf: user.professional?.cpf,
        cref: user.professional?.cref,
        birth_date: user.professional?.birth_date,
        email: user.email,
        cellphone: user.cellphone,
        address_zip_code: user.address_zip_code,
        address_street: user.address_street,
        address_number: user.address_number,
        address_complement: user.address_complement,
        address_city: user.address_city,
        address_state: user.address_state,
        address_country: user.address_country,
      });
      formRef.current?.setFieldValue('address_number', user.address_number);
      formRef.current?.setFieldValue('address_state', user.address_state);
      handleGetCities(user.address_state).then(() => {
        formRef.current?.setFieldValue('address_city', user.address_city);
      });
    }
  }, [formRef, handleGetCities, user]);

  return (
    <S.EditUserForm
      id={formName}
      ref={formRef}
      initialData={initialData}
      onSubmit={handleSubmit}
    >
      <div style={{ gridColumnStart: '1', gridColumnEnd: '3' }}>
        <S.EditUserBoxImageLabel htmlFor="image_url">
          Logo
        </S.EditUserBoxImageLabel>
        <S.EditUserBoxImage>
          <img
            src={uploadImage ? uploadImage.name : IconCamera}
            alt=""
            style={{
              width: uploadImage ? 'auto' : '46px',
              height: uploadImage ? 'auto' : '36px',
            }}
          />
          <input
            type="file"
            accept="image/jpeg,image/png"
            id="image_url"
            name="image_url"
            onChange={handleSetImage}
            hidden
          />
        </S.EditUserBoxImage>
      </div>
      <Input name="public_name" label="Nome público" />
      <Input name="name" label="Nome" />
      <S.EditUserInputBlock>
        <p>CPF</p>
        <InputMask name="cpf" mask="999.999.999-99" />
      </S.EditUserInputBlock>
      <Input name="cref" label="Registro Profissional" />
      <S.EditUserInputBlock>
        <p>Data de Nascimento</p>
        <InputMask
          type="tel"
          name="birth_date"
          placeholder="dd/mm/aaaa"
          mask="99/99/9999"
        />
      </S.EditUserInputBlock>
      <Input name="email" label="E-mail" />
      <S.EditUserInputBlock htmlFor="cellphone">
        <p>Celular</p>
        <InputMask name="cellphone" mask="99 99999-9999" />
      </S.EditUserInputBlock>
      <S.EditUserInputBlock htmlFor="zip_code">
        <p>CEP</p>
        <InputMask
          name="address_zip_code"
          onBlur={handleCEP}
          mask="99999-999"
        />
      </S.EditUserInputBlock>
      <Input name="address_street" label="Endereço" />
      <Input type="number" name="address_number" label="Número" />
      <Input name="address_complement" label="Complemento" />
      <S.EditUserInputBlock htmlFor="address_city">
        <p>Cidade</p>
        <Select name="address_city" options={citiesOptionsMemo} />
      </S.EditUserInputBlock>
      <S.EditUserInputBlock htmlFor="address_state">
        <p>Estado</p>
        <Select
          name="address_state"
          onChange={(e) => handleGetCities(e.target.value)}
          options={statesOptionsMemo}
        />
      </S.EditUserInputBlock>
      <Input name="address_country" label="País" />
    </S.EditUserForm>
  );
}

EditUserForm.defaultProps = {
  userId: undefined,
};

export default EditUserForm;
